import React, { Suspense, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { useRillContext } from "../../rill/RillReact/share/base";
import SvgIcon from "../SvgIcon";
import NewsDrawerItem from "./NewsDrawerItem";
import NewsDrawerSkeleton from "./NewsDrawerSkeleton";

const Backdrop = ({ close }) => (
  <div className="drawer-backdrop" onClick={close} />
);

const NewsDrawer = () => {
  const { t } = useTranslation("news");
  const { locals } = useRillContext();
  const { listNews } = locals;

  // The loader fetches from skip 0 with limit = newsSkip + NEWS_PAGE_SIZE, so
  // `listNews` already holds every announcement that should be on screen. Render
  // it directly: keeping a copy in component state meant the server render (where
  // effects never run) always emitted the empty state, and any refresh that
  // arrived without changing `newsSkip` left that copy stale.
  const hasLoaded = Boolean(listNews);
  const docs = listNews?.docs || [];
  const total = listNews?.total || 0;

  const close = useCallback(() => {
    submit("/actions/refresh", {
      method: "GET",
      body: { modal: "", newsSkip: "" },
    });
  }, []);

  const loadMore = useCallback(() => {
    submit("/actions/refresh", {
      method: "GET",
      body: { newsSkip: docs.length },
    });
  }, [docs.length]);

  // Mark all visible items as read whenever a new page arrives.
  // `/actions/news/mark-read` responds with a full refreshWith navigation, so this
  // must never fire for an id twice: keying on the array identity re-ran it on
  // every render, and it only stopped because the refetched docs came back read.
  // If that write is not visible to the following read the POST/refresh cycle
  // repeats, so track what has already been sent and key on the ids themselves.
  const markedRef = useRef(new Set());
  const unreadKey = docs
    .filter((d) => !d.read)
    .map((d) => d._id)
    .join(",");

  useEffect(() => {
    if (!unreadKey) return;
    const toMark = unreadKey
      .split(",")
      .filter((id) => !markedRef.current.has(id));
    if (!toMark.length) return;

    for (const id of toMark) markedRef.current.add(id);
    submit("/actions/news/mark-read", {
      method: "POST",
      body: { newsIds: toMark },
    });
  }, [unreadKey]);

  return (
    <>
      <Backdrop close={close} />
      <div className="news-drawer">
        <div className="news-drawer-header">
          <h2>{t("title")}</h2>
          <SvgIcon
            src="/img/new-icons/close-modal.svg"
            onClickFn={close}
            noFilterOnLight
            classNames="cursor-pointer"
          />
        </div>

        <div className="news-drawer-body">
          {hasLoaded && total === 0 && (
            <p className="news-drawer-empty">{t("empty")}</p>
          )}
          <Suspense
            fallback={<NewsDrawerSkeleton count={Math.min(docs.length, 3)} />}
          >
            {docs.map((item, i) => (
              <React.Fragment key={item._id}>
                {i > 0 && <hr className="news-drawer-divider" />}
                <NewsDrawerItem item={item} />
              </React.Fragment>
            ))}
          </Suspense>

          {docs.length < total && (
            <button
              className="news-drawer-load-more"
              onClick={loadMore}
              type="button"
            >
              {t("loadMore")}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default NewsDrawer;
